import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CheckCircle2, Clock, TimerOff, XCircle } from 'lucide-react';
import type { SubmitResult } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { formatDuration, formatPercent } from '@/lib/format';

type ResultLocationState = {
  result?: SubmitResult;
  expired?: boolean;
  /** มาถึงหน้านี้เพราะ server บอกว่า attempt ถูกส่งไปแล้ว (ไม่มีคะแนนติดมือมาด้วย) */
  alreadySubmitted?: boolean;
};

/**
 * หน้าปลายทางหลังส่งคำตอบ — รวมกรณีหมดเวลาและกรณีที่ส่งไปแล้วด้วย
 * หมดเวลาไม่ใช่ error: ต้องบอกให้ชัดว่าระบบส่งคำตอบให้แล้ว
 * และ "ส่งไปแล้ว" ก็ไม่ใช่หมดเวลา — ห้ามขึ้นว่าหมดเวลาให้คนที่กดส่งเอง
 */
export function ResultPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { token = '' } = useParams();
  const state = (location.state ?? {}) as ResultLocationState;
  // มาจากลิงก์ส่วนตัว → พากลับหน้าตัวเองได้ เผื่อยังมีอีกชุดที่ต้องทำ (เช่น post-test)
  const fromInvite = location.pathname.startsWith('/i/');
  const result = state.result;
  const expired = state.expired || result?.status === 'expired';
  const alreadySubmitted = Boolean(state.alreadySubmitted) && !expired;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-5 px-5 py-8">
      <header className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-slate-500">{t('app.org')}</p>
        <LanguageSwitcher />
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-6 text-center">
        {expired ? (
          <TimerOff aria-hidden className="mx-auto h-10 w-10 text-amber-600" />
        ) : (
          <CheckCircle2 aria-hidden className="mx-auto h-10 w-10 text-emerald-600" />
        )}
        <h1 className="mt-3 text-xl font-semibold text-slate-900">
          {expired ? t('result.expiredTitle') : t('result.title')}
        </h1>
        <p className="mt-2 text-slate-700">
          {expired
            ? t('result.expiredBody')
            : alreadySubmitted
              ? t('result.alreadySubmittedBody')
              : t('result.thanks')}
        </p>
      </section>

      {!result && !alreadySubmitted && (
        <p className="rounded-xl border border-slate-200 bg-white p-5 text-slate-700">
          {t('result.noSession')}
        </p>
      )}

      {result && !result.showResult && (
        <p className="rounded-xl border border-slate-200 bg-white p-5 text-slate-700">
          {t('result.hiddenResult')}
        </p>
      )}

      {result?.result && (
        <section className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-sm font-medium text-slate-600">{t('result.score')}</h2>
          <p className="mt-1 text-3xl font-semibold text-slate-900">
            {formatPercent(result.result.percentScore)}
          </p>
          <p className="mt-1 text-slate-700">
            {t('result.scoreValue', { raw: result.result.rawScore, max: result.result.maxScore })}
          </p>

          {result.result.isPassed !== null && (
            <p
              className={
                result.result.isPassed
                  ? 'mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-800'
                  : 'mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-900'
              }
            >
              {result.result.isPassed ? (
                <CheckCircle2 aria-hidden className="h-4 w-4" />
              ) : (
                <XCircle aria-hidden className="h-4 w-4" />
              )}
              {result.result.isPassed ? t('result.passed') : t('result.notPassed')}
            </p>
          )}

          <p className="mt-4 flex items-center gap-1.5 text-sm text-slate-600">
            <Clock aria-hidden className="h-4 w-4" />
            {t('result.duration')}: {formatDuration(result.durationSeconds)}
          </p>
        </section>
      )}

      {result?.result?.questions && result.result.questions.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-base font-semibold text-slate-900">{t('result.reviewTitle')}</h2>
          <ol className="mt-3 flex flex-col gap-3">
            {result.result.questions.map((item, index) => (
              <li
                key={item.questionId}
                className="border-t border-slate-100 pt-3 first:border-0 first:pt-0"
              >
                <p className="flex items-center gap-1.5 font-medium">
                  <span className="text-slate-500">
                    {t('common.question')} {index + 1}
                  </span>
                  {item.isCorrect ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 aria-hidden className="h-4 w-4" />
                      {t('result.correct')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700">
                      <XCircle aria-hidden className="h-4 w-4" />
                      {t('result.incorrect')}
                    </span>
                  )}
                </p>
                {item.explanation && (
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                    {item.explanation}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {fromInvite && (
        <Link
          to={`/i/${token}`}
          className="rounded-xl border border-slate-200 bg-white p-4 text-center font-medium text-teal-700 underline"
        >
          {t('result.backToMyPage')}
        </Link>
      )}
    </main>
  );
}
